import { KeyboardEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { TFunction } from 'i18next';
import { useReceiptPicker } from '../../hooks/useReceiptPicker';
import type { Transaction } from '../../services/types';
import type { ChatInput } from '../../services/conciergeChatTypes';
import QR from '../atoms/QR/QR';
import { CHAT } from './tokens';

const LINE_PX = 20;
const PAD_PX = 16;

/** Messenger-like: the field grows with the text up to a cap that follows the screen's height. */
export const composerMaxLines = (viewportHeight: number): 3 | 5 => (viewportHeight < 700 ? 3 : 5);

const finePointer = (): boolean => {
  try {
    return window.matchMedia('(pointer: fine)').matches;
  } catch {
    return false;
  }
};

const Bar = styled.div`
  flex: none;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid ${CHAT.line};
  position: relative;
`;
const Round = styled.button<{ $fill?: boolean }>`
  flex: none;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: ${({ $fill }) => ($fill ? CHAT.action : CHAT.surface)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s ease-out, opacity 0.15s, filter 0.15s;
  -webkit-tap-highlight-color: transparent;
  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
  &:active:not(:disabled) {
    transform: scale(0.88);
    filter: brightness(0.9);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
const Field = styled.textarea`
  flex: 1;
  min-width: 0;
  resize: none;
  border: 0;
  outline: none;
  border-radius: 18px;
  background: ${CHAT.grey};
  padding: 8px 12px;
  /* 16px or iOS zooms the page on focus. */
  font-size: 16px;
  line-height: ${LINE_PX}px;
  font-family: ${CHAT.sans};
  color: ${CHAT.ink};
  overflow-y: hidden;
`;
const Menu = styled.div`
  position: absolute;
  left: 12px;
  bottom: 54px;
  width: 230px;
  padding: 6px;
  background: #fff;
  border: 1px solid ${CHAT.line};
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 10;
  button {
    width: 100%;
    padding: 9px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    font-size: 14px;
    font-family: ${CHAT.sans};
    cursor: pointer;
  }
  button:hover {
    background: ${CHAT.surface};
  }
`;
const MenuQr = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
`;
const ReceiptHint = styled.button`
  flex: 1;
  align-self: center;
  padding: 8px 0;
  border: 0;
  background: none;
  text-align: left;
  font-family: ${CHAT.sans};
  font-size: 15px;
  color: ${CHAT.actionText};
  cursor: pointer;
`;
const ErrorText = styled.div`
  flex: none;
  padding: 0 14px 6px;
  font-size: 13px;
  color: ${CHAT.warn};
`;

interface Props {
  /** `receipt_only`: the clip alone, to send the receipt after time ran out. */
  input: Exclude<ChatInput, 'closed'>;
  uuid: string;
  transaction: Transaction;
  t: TFunction;
  desktop: boolean;
  /** The conversation cannot be read: no text, but a file still goes to the deposit. */
  disabled: boolean;
  phoneUrl: string;
  focusAttach: boolean;
  /** Takes the text at once: the chat draws it and reports its delivery itself. */
  onSend: (text: string) => void;
  /** Takes the file at once: the chat draws it and reports its upload itself. */
  onFile: (file: File) => void;
  /** The field changed its height: the feed keeps its bottom in view if it was there. */
  onResize: () => void;
}

const Composer = ({ input, uuid, transaction, t, desktop, disabled, phoneUrl, focusAttach, onSend, onFile, onResize }: Props) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<'closed' | 'open' | 'qr'>('closed');
  const [maxLines, setMaxLines] = useState(() => composerMaxLines(window.innerHeight));
  const field = useRef<HTMLTextAreaElement>(null);
  const attach = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onWindowResize = () => setMaxLines(composerMaxLines(window.innerHeight));
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  useEffect(() => {
    if (focusAttach) attach.current?.focus();
  }, [focusAttach]);

  // Grow with the text up to the cap, then scroll inside; the end of the text stays in view.
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    const max = maxLines * LINE_PX + PAD_PX;
    el.style.height = 'auto';
    const height = Math.min(el.scrollHeight, max);
    el.style.height = `${height}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
    el.scrollTop = el.scrollHeight;
    onResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, maxLines]);

  const picker = useReceiptPicker({
    uuid,
    transaction,
    onFile: (file) => {
      setError(null);
      onFile(file);
    },
    onError: (key) => setError(t(key)),
    onBusy: setBusy,
    onPickStart: () => setError(null),
  });

  // As in a messenger: the field empties and the message is in the feed at once; the keyboard stays.
  const send = useCallback(() => {
    const value = text.trim();
    if (!value || disabled) return;
    setText('');
    onSend(value);
    field.current?.focus();
  }, [text, disabled, onSend]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && finePointer()) {
      e.preventDefault();
      send();
    }
  };

  const onAttach = () => {
    if (!desktop) {
      picker.open();
      return;
    }
    setMenu((m) => (m === 'closed' ? 'open' : 'closed'));
  };

  return (
    <>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <Bar>
        {menu !== 'closed' && (
          <Menu>
            <button type='button' onClick={() => { setMenu('closed'); picker.open(); }}>📁 {t('concierge.chat.composer.fromComputer')}</button>
            <button type='button' onClick={() => setMenu(menu === 'qr' ? 'open' : 'qr')}>📱 {t('concierge.chat.composer.fromPhone')}</button>
            {menu === 'qr' && (
              <MenuQr>
                <QR data={phoneUrl} size={120} bgColor='#ffffff' fgColor='#111111' frameColor='#ffffff' />
              </MenuQr>
            )}
          </Menu>
        )}
        <Round ref={attach} type='button' aria-label={t('concierge.chat.composer.attach')} disabled={busy} onClick={onAttach}>
          <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
            <path fill={CHAT.actionText} d='M16.5 6v11.5a4 4 0 0 1-8 0V5a2.5 2.5 0 0 1 5 0v10.5a1 1 0 0 1-2 0V6H10v9.5a2.5 2.5 0 0 0 5 0V5a4 4 0 0 0-8 0v12.5a5.5 5.5 0 0 0 11 0V6h-1.5z' />
          </svg>
        </Round>
        {input === 'receipt_only' ? (
          <ReceiptHint type='button' onClick={onAttach}>
            {t('concierge.chat.composer.sendReceipt')}
          </ReceiptHint>
        ) : (
          <>
            <Field
              ref={field}
              rows={1}
              value={text}
              disabled={disabled}
              placeholder={t('concierge.chat.composer.placeholder')}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <Round $fill type='button' aria-label={t('concierge.chat.composer.send')} disabled={disabled || text.trim() === ''} onMouseDown={(e) => e.preventDefault()} onClick={send}>
              <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
                <path fill='#fff' d='M2 21l21-9L2 3v7l15 2-15 2z' />
              </svg>
            </Round>
          </>
        )}
      </Bar>
    </>
  );
};

export default Composer;
